<template>
  <div>
    <b-card>
      <b-row class="mb-2">
        <b-col>
          <b-button variant="primary" @click="openAddModal">+ Add Price</b-button>
        </b-col>
      </b-row>

      <!-- Loading skeleton -->
      <div v-if="loading">
        <b-skeleton width="40%" height="2rem" class="mb-2"/>
        <b-skeleton width="70%" class="mb-1"/>
        <b-skeleton-table :rows="5" :columns="4" hide-header/>
      </div>

      <div v-else-if="!items.length" class="d-flex flex-column align-items-center py-5">
        <b-img
          :src="require('@/assets/images/svg/infographics/no_data_infographic.svg')"
          width="140"
        />
        <h5 class="mt-2 text-secondary">No market prices yet</h5>
        <p class="text-muted mb-0">Add the first price to start tracking trends.</p>
      </div>

      <b-table
        v-else
        :items="items"
        :fields="fields"
        striped
        hover
        responsive
      >
        <template #cell(price_date)="data">
          {{ data.value ? data.value.toString().substring(0, 10) : '—' }}
        </template>
        <template #cell(price_per_kg)="data">
          ₹{{ data.value }}
        </template>
        <template #cell(actions)="data">
          <b-button size="sm" variant="outline-primary" class="mr-1" @click="openEditModal(data.item)">Edit</b-button>
          <b-button size="sm" variant="outline-danger" @click="deleteItem(data.item.id)">Delete</b-button>
        </template>
      </b-table>

      <!-- Pagination -->
      <b-row v-if="totalRows > perPage">
        <b-col class="d-flex justify-content-end">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            @change="fetchData"
          />
        </b-col>
      </b-row>
    </b-card>

    <!-- Add/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="editItem ? 'Edit Market Price' : 'Add Market Price'"
      ok-title="Save"
      @ok="saveItem"
      @hidden="resetForm"
    >
      <b-form-group label="Bird Type">
        <b-form-select v-model="form.bird_type" :options="birdTypeOptions" :state="errors.bird_type ? false : null" />
        <small v-if="errors.bird_type" class="text-danger">{{ errors.bird_type }}</small>
      </b-form-group>
      <b-form-group label="Region">
        <b-form-input v-model="form.region" placeholder="e.g. North India" :state="errors.region ? false : null" />
        <small v-if="errors.region" class="text-danger">{{ errors.region }}</small>
      </b-form-group>
      <b-form-group label="Price per kg (₹)">
        <b-form-input v-model="form.price_per_kg" type="number" step="0.01" min="0" :state="errors.price_per_kg ? false : null" />
        <small v-if="errors.price_per_kg" class="text-danger">{{ errors.price_per_kg }}</small>
      </b-form-group>
      <b-form-group label="Price Date">
        <b-form-input v-model="form.price_date" type="date" :state="errors.price_date ? false : null" />
        <small v-if="errors.price_date" class="text-danger">{{ errors.price_date }}</small>
      </b-form-group>
      <b-form-group label="Notes">
        <b-form-input v-model="form.notes" placeholder="Optional notes" />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import {
  BCard, BRow, BCol, BTable, BButton, BSpinner, BModal,
  BFormGroup, BFormInput, BFormSelect, BPagination,
} from 'bootstrap-vue'

export default {
  name: 'MarketPriceList',
  components: {
    BCard, BRow, BCol, BTable, BButton, BSpinner, BModal,
    BFormGroup, BFormInput, BFormSelect, BPagination,
  },
  data() {
    return {
      loading: false,
      items: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 15,
      showModal: false,
      editItem: null,
      errors: {},
      form: {
        bird_type: 'broiler',
        region: '',
        price_per_kg: '',
        price_date: '',
        notes: '',
      },
      fields: [
        { key: 'price_date', label: 'Date', sortable: true },
        { key: 'bird_type', label: 'Bird Type' },
        { key: 'region', label: 'Region' },
        { key: 'price_per_kg', label: 'Price/kg' },
        { key: 'actions', label: 'Actions' },
      ],
      birdTypeOptions: [
        { value: 'broiler', text: 'Broiler' },
        { value: 'layer', text: 'Layer' },
        { value: 'breeder', text: 'Breeder' },
      ],
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const { data } = await axios.get('web/market-prices', {
          params: { page: this.currentPage, per_page: this.perPage },
        })
        this.items = data.data || data.items || []
        this.totalRows = data.total || this.items.length
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to load market prices', { variant: 'danger', title: 'Error' })
      } finally {
        this.loading = false
      }
    },
    openAddModal() {
      this.editItem = null
      this.resetForm()
      this.showModal = true
    },
    openEditModal(item) {
      this.editItem = item
      this.form = {
        ...item,
        price_date: item.price_date ? item.price_date.toString().substring(0, 10) : '',
      }
      this.showModal = true
    },
    resetForm() {
      this.form = { bird_type: 'broiler', region: '', price_per_kg: '', price_date: '', notes: '' }
      this.editItem = null
      this.errors = {}
    },
    validateForm() {
      this.errors = {}
      if (!this.form.price_per_kg || parseFloat(this.form.price_per_kg) <= 0) {
        this.errors.price_per_kg = 'Price must be greater than 0'
      }
      if (!this.form.region || this.form.region.trim() === '') {
        this.errors.region = 'Region is required'
      }
      if (!this.form.bird_type) {
        this.errors.bird_type = 'Bird type is required'
      }
      if (!this.form.price_date) {
        this.errors.price_date = 'Date is required'
      }
      return Object.keys(this.errors).length === 0
    },
    async saveItem(bvModalEvt) {
      bvModalEvt.preventDefault()
      if (!this.validateForm()) return
      try {
        if (this.editItem) {
          await axios.put(`web/market-prices/${this.editItem.id}`, this.form)
          this.$bvToast.toast('Price updated', { variant: 'success', title: 'Success' })
        } else {
          await axios.post('web/market-prices', this.form)
          this.$bvToast.toast('Price added', { variant: 'success', title: 'Success' })
        }
        this.showModal = false
        this.fetchData()
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to save price', { variant: 'danger', title: 'Error' })
      }
    },
    async deleteItem(id) {
      if (!confirm('Delete this price entry?')) return
      try {
        await axios.delete(`web/market-prices/${id}`)
        this.$bvToast.toast('Deleted', { variant: 'success', title: 'Success' })
        this.fetchData()
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to delete', { variant: 'danger', title: 'Error' })
      }
    },
  },
}
</script>

<style scoped></style>
